const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const loadImageFromUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to download source image (${response.status}).`);
  const objectUrl = URL.createObjectURL(await response.blob());

  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Failed to decode the source image.'));
      element.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const pointOnCanvas = (point, width, height) => ({
  x: (clampPercent(point.xPercent ?? point.x) / 100) * width,
  y: (clampPercent(point.yPercent ?? point.y) / 100) * height
});

const drawMarker = (context, marker, width, height) => {
  const { x, y } = pointOnCanvas(marker, width, height);
  const scale = Math.max(1, Math.min(width, height) / 900);
  context.save();
  context.strokeStyle = marker.color || '#dc2626';
  context.lineWidth = 5 * scale;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.beginPath();
  if (marker.type === 'tick') {
    context.moveTo(x - (11 * scale), y);
    context.lineTo(x - (2 * scale), y + (12 * scale));
    context.lineTo(x + (22 * scale), y - (17 * scale));
  } else {
    context.moveTo(x - (12 * scale), y - (12 * scale));
    context.lineTo(x + (12 * scale), y + (12 * scale));
    context.moveTo(x + (12 * scale), y - (12 * scale));
    context.lineTo(x - (12 * scale), y + (12 * scale));
  }
  context.stroke();
  context.restore();
};

const drawPenStroke = (context, stroke, width, height) => {
  const points = Array.isArray(stroke.points) ? stroke.points : [];
  if (points.length === 0) return;
  const scale = Math.max(1, Math.min(width, height) / 900);
  context.save();
  context.strokeStyle = stroke.color || '#dc2626';
  context.lineWidth = 3 * scale;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.beginPath();
  points.forEach((point, index) => {
    const canvasPoint = pointOnCanvas(point, width, height);
    if (index === 0) context.moveTo(canvasPoint.x, canvasPoint.y);
    else context.lineTo(canvasPoint.x, canvasPoint.y);
  });
  context.stroke();
  context.restore();
};

const drawAnnotation = (context, annotation, width, height) => {
  const { x, y } = pointOnCanvas(annotation, width, height);
  const scale = Math.max(1, Math.min(width, height) / 900);
  const title = `Q${annotation.questionLabel || '-'} ${Number(annotation.marksAwarded || 0)}/${Number(annotation.marksAvailable || 0)}`;
  const comment = String(annotation.comment || '').trim();
  const fontSize = 14 * scale;
  context.save();
  context.font = `600 ${fontSize}px sans-serif`;
  const boxWidth = Math.max(110 * scale, context.measureText(title).width + (16 * scale));
  const boxHeight = comment ? 48 * scale : 30 * scale;
  const left = Math.max(0, Math.min(width - boxWidth, x - (boxWidth / 2)));
  const top = Math.max(0, Math.min(height - boxHeight, y - (boxHeight / 2)));
  context.fillStyle = 'rgba(254, 240, 138, 0.95)';
  context.strokeStyle = '#eab308';
  context.lineWidth = Math.max(1, scale);
  context.fillRect(left, top, boxWidth, boxHeight);
  context.strokeRect(left, top, boxWidth, boxHeight);
  context.fillStyle = '#111827';
  context.fillText(title, left + (8 * scale), top + (19 * scale));
  if (comment) {
    context.font = `${12 * scale}px sans-serif`;
    context.fillText(comment.slice(0, 80), left + (8 * scale), top + (39 * scale), boxWidth - (16 * scale));
  }
  context.restore();
};

export const generateMarkedImageBlob = async ({ sourceUrl, results, pageNumber, format }) => {
  const image = await loadImageFromUrl(sourceUrl);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image export is not supported by this browser.');

  if (format === 'jpeg') {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const annotations = results?.pageResults?.find((page) => page.pageNumber === pageNumber)?.annotations || [];
  const icons = results?.markerIcons?.find((page) => page.pageNumber === pageNumber)?.icons || [];
  const strokes = results?.penStrokes?.find((page) => page.pageNumber === pageNumber)?.strokes || [];
  annotations.forEach((annotation) => drawAnnotation(context, annotation, canvas.width, canvas.height));
  icons.forEach((icon) => drawMarker(context, icon, canvas.width, canvas.height));
  strokes.forEach((stroke) => drawPenStroke(context, stroke, canvas.width, canvas.height));

  const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Failed to create the marked image.'))),
      mimeType,
      format === 'jpeg' ? 0.92 : undefined
    );
  });
};
